import { useState, useEffect, useRef } from "react";
import { useLocation, Link, useSearch } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { getGetMeQueryKey, useSignup } from "@workspace/api-client-react";
import { trackSignup } from "@/lib/metrics";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Check, ArrowLeft, Loader2, Shield } from "lucide-react";
import { PhoneInput } from "@/components/PhoneInput";
import { cn } from "@/lib/utils";
import { COUNTRIES, CURRENCIES } from "@/lib/iso";

const PLANS = [
  { id: "lite",     label: "Lite",     priceMonthly: 10,  priceYearly: 100,  discountPct: 17, desc: "15 projects · 10 users · Full invoicing, BOQ, VOs",        popular: false },
  { id: "lite-pro", label: "Lite Pro", priceMonthly: 25,  priceYearly: 250,  discountPct: 17, desc: "35 projects · 15 users · + Reports, CFO View, Retention",   popular: false },
  { id: "pro",      label: "Pro",      priceMonthly: 60,  priceYearly: 600,  discountPct: 17, desc: "75 projects · 25 users · + Multi-company, API, Branding",   popular: true  },
  { id: "pro-plus", label: "Pro Plus", priceMonthly: 100, priceYearly: 1000, discountPct: 17, desc: "120 projects · Unlimited users · + Double-entry Accounting", popular: false },
];

type Step = "account" | "otp" | "company" | "plan" | "payment";
const STEPS: Step[] = ["account", "otp", "company", "plan", "payment"];

function stepIndex(s: Step) { return STEPS.indexOf(s); }

const API_BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

export default function Signup() {
  const search = useSearch();
  const params = new URLSearchParams(search);
  const emailFromQuery = params.get("email") ?? "";

  const [step, setStep] = useState<Step>("account");
  const [billingCycle, setBillingCycle] = useState<"monthly" | "yearly">("monthly");
  const [form, setForm] = useState({
    name: "", email: emailFromQuery,
    companyName: "", country: "United States", currency: "USD",
    phone: "", plan: "pro",
  });
  const [loading, setLoading] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [isDemoAccount, setIsDemoAccount] = useState(false);
  const [demoCompanyName, setDemoCompanyName] = useState<string | null>(null);
  const [emailChecked, setEmailChecked] = useState("");
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const qc = useQueryClient();
  const signup = useSignup();
  const autoCheckedRef = useRef(false);

  const set = (key: string, val: string) => setForm(prev => ({ ...prev, [key]: val }));

  const checkDemoEmail = async (email: string) => {
    if (!email || emailChecked === email) return;
    setEmailChecked(email);
    try {
      const res = await fetch(`${API_BASE}/api/auth/check-demo?email=${encodeURIComponent(email)}`);
      const data = await res.json() as { isDemo: boolean; companyName?: string };
      setIsDemoAccount(data.isDemo);
      setDemoCompanyName(data.companyName || null);
    } catch {
      setIsDemoAccount(false);
    }
  };

  useEffect(() => {
    if (emailFromQuery && !autoCheckedRef.current) {
      autoCheckedRef.current = true;
      checkDemoEmail(emailFromQuery);
    }
  }, [emailFromQuery]);

  const handleAccountNext = async (e: React.FormEvent) => {
    e.preventDefault();
    setOtpSending(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: form.email }),
      });
      const data = await res.json() as { error?: string };
      if (!res.ok) {
        toast({ variant: "destructive", title: "Cannot send code", description: data.error || "Please try again." });
        return;
      }
      await checkDemoEmail(form.email);
      setOtpCode("");
      setStep("otp");
    } catch {
      toast({ variant: "destructive", title: "Network error", description: "Please check your connection and try again." });
    } finally {
      setOtpSending(false);
    }
  };

  const handleResendOtp = async () => {
    setOtpSending(true);
    try {
      await fetch(`${API_BASE}/api/auth/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: form.email }),
      });
      toast({ title: "Code resent", description: `A new code was sent to ${form.email}` });
    } finally {
      setOtpSending(false);
    }
  };

  const handleOtpNext = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otpCode.length !== 6) {
      toast({ variant: "destructive", title: "Invalid code", description: "Please enter the 6-digit code." });
      return;
    }
    setOtpVerifying(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: form.email, code: otpCode }),
      });
      const data = await res.json() as { verified?: boolean; error?: string };
      if (!res.ok || !data.verified) {
        toast({ variant: "destructive", title: "Wrong code", description: data.error || "Please check the code and try again." });
        return;
      }
      setStep("company");
    } catch {
      toast({ variant: "destructive", title: "Network error", description: "Please try again." });
    } finally {
      setOtpVerifying(false);
    }
  };

  const handleCompanyNext = (e: React.FormEvent) => {
    e.preventDefault();
    setStep("plan");
  };

  const handlePlanNext = () => {
    setStep("payment");
  };

  const handleActivate = async () => {
    setLoading(true);
    try {
      await signup.mutateAsync({ data: { name: form.name, email: form.email, companyName: form.companyName, country: form.country, currency: form.currency, plan: form.plan } });
      trackSignup();
      await qc.invalidateQueries({ queryKey: getGetMeQueryKey() });
      setLocation("/app/setup");
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast({
        variant: "destructive",
        title: "Activation Failed",
        description: msg || "Something went wrong. Please try again.",
      });
      setLoading(false);
    }
  };

  const selectedPlan = PLANS.find(p => p.id === form.plan)!;

  return (
    <div className="min-h-screen flex bg-slate-950">
      {/* Left Panel */}
      <div className="hidden lg:flex w-[400px] flex-col bg-gradient-to-b from-blue-600 to-violet-800 p-12 relative overflow-hidden shrink-0">
        <div className="absolute inset-0">
          <svg className="absolute inset-0 w-full h-full opacity-5" viewBox="0 0 400 600" xmlns="http://www.w3.org/2000/svg">
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="1" />
            </pattern>
            <rect width="400" height="600" fill="url(#grid)" />
          </svg>
        </div>
        <div className="relative z-10">
          <Link href="/app/login" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm transition-colors mb-12">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to login
          </Link>
          <div className="mb-8">
            <img src="/images/finproject-logo.png" alt="FinProject" className="h-14 w-14 rounded-2xl object-cover" style={{ border: "2px solid rgba(255,255,255,0.2)", background: "#ffffff" }} />
          </div>
          <h2 className="text-2xl font-bold text-white mb-3">Start your account</h2>
          <p className="text-blue-100 text-sm leading-relaxed mb-8">Choose a plan and activate your account instantly.</p>
          <div className="space-y-4">
            {["Client invoicing & retention", "Subcontractor management", "Payroll processing", "Cash flow forecasting", "BOQ & budget tracking", "Multi-currency support"].map((f, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                  <Check className="w-3 h-3 text-white" />
                </div>
                <span className="text-blue-50 text-sm">{f}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 mt-auto">
          <div className="flex gap-2">
            {STEPS.map((s) => (
              <div key={s} className={cn("h-1.5 rounded-full transition-all", s === step ? "bg-white flex-1" : stepIndex(s) < stepIndex(step) ? "bg-white/60 w-6" : "bg-white/30 w-6")} />
            ))}
          </div>
          <p className="text-blue-200 text-xs mt-3">
            Step {stepIndex(step) + 1} of {STEPS.length}
          </p>
        </div>
      </div>

      {/* Right Panel */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-lg">
          {step === "account" && (
            <>
              <h1 className="text-3xl font-bold text-white mb-2">Create your account</h1>
              <p className="text-slate-400 mb-8">Start with your personal details</p>
              <form onSubmit={handleAccountNext} className="space-y-5">
                <div className="space-y-2">
                  <Label className="text-slate-300">Full name</Label>
                  <Input value={form.name} onChange={e => set("name", e.target.value)} placeholder="Alex Johnson" required
                    className="h-12 rounded-xl bg-slate-900 border-slate-700 text-white placeholder:text-slate-600" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Work email</Label>
                  <Input type="email" value={form.email} onChange={e => set("email", e.target.value)} placeholder="you@company.com" required
                    className="h-12 rounded-xl bg-slate-900 border-slate-700 text-white placeholder:text-slate-600" />
                </div>
                <Button type="submit" className="w-full h-12 rounded-xl bg-gradient-to-r from-blue-600 to-violet-600 text-white font-semibold text-base border-0 mt-2">
                  Continue
                </Button>
              </form>
              <p className="text-center text-sm text-slate-500 mt-6">
                Already have an account?{" "}
                <Link href="/app/login" className="text-blue-400 hover:text-blue-300 font-medium">Sign in</Link>
              </p>
            </>
          )}

          {step === "otp" && (
            <>
              <button onClick={() => setStep("account")} className="text-slate-400 hover:text-white flex items-center gap-1.5 text-sm mb-8 transition-colors">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <h1 className="text-3xl font-bold text-white mb-2">Verify your email</h1>
              <p className="text-slate-400 mb-2">We sent a 6-digit code to</p>
              <p className="text-blue-400 font-semibold mb-8">{form.email}</p>
              <form onSubmit={handleOtpNext} className="space-y-5">
                <div className="space-y-2">
                  <Label className="text-slate-300">Verification code</Label>
                  <Input
                    value={otpCode}
                    onChange={e => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="000000"
                    maxLength={6}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    className="h-14 rounded-xl bg-slate-900 border-slate-700 text-white text-center text-2xl font-mono tracking-[0.5em] placeholder:text-slate-700 placeholder:tracking-normal"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={otpVerifying || otpCode.length !== 6}
                  className="w-full h-12 rounded-xl bg-gradient-to-r from-blue-600 to-violet-600 text-white font-semibold text-base border-0 mt-2"
                >
                  {otpVerifying ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying...</> : "Verify & continue"}
                </Button>
              </form>
              <p className="text-center text-sm text-slate-500 mt-6">
                Didn't get it?{" "}
                <button
                  onClick={handleResendOtp}
                  disabled={otpSending}
                  className="text-blue-400 hover:text-blue-300 font-medium disabled:opacity-50"
                >
                  {otpSending ? "Sending..." : "Resend code"}
                </button>
              </p>
            </>
          )}

          {step === "company" && (
            <>
              <button onClick={() => setStep("otp")} className="text-slate-400 hover:text-white flex items-center gap-1.5 text-sm mb-8 transition-colors">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <h1 className="text-3xl font-bold text-white mb-2">Set up your company</h1>
              <p className="text-slate-400 mb-4">We'll create your company workspace</p>

              {isDemoAccount && (
                <div className="mb-6 p-4 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-amber-400" />
                  </div>
                  <div>
                    <div className="text-amber-400 font-semibold text-sm">Demo account found</div>
                    <div className="text-amber-300/80 text-xs mt-0.5">
                      We found your demo account{demoCompanyName ? ` (${demoCompanyName})` : ""} — your existing data will be kept when you upgrade.
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleCompanyNext} className="space-y-5">
                <div className="space-y-2">
                  <Label className="text-slate-300">Company name</Label>
                  <Input value={form.companyName} onChange={e => set("companyName", e.target.value)} placeholder="Acme Construction Ltd." required
                    className="h-12 rounded-xl bg-slate-900 border-slate-700 text-white placeholder:text-slate-600" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Country</Label>
                    <Select value={form.country} onValueChange={v => set("country", v)}>
                      <SelectTrigger className="h-12 rounded-xl bg-slate-900 border-slate-700 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        {COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Currency</Label>
                    <Select value={form.currency} onValueChange={v => set("currency", v)}>
                      <SelectTrigger className="h-12 rounded-xl bg-slate-900 border-slate-700 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        {CURRENCIES.map(c => <SelectItem key={c.code} value={c.code}>{c.code} — {c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Phone / Mobile <span className="text-slate-500 font-normal text-xs">(optional)</span></Label>
                  <PhoneInput
                    name="phone"
                    defaultValue={form.phone}
                    country={form.country}
                    onChange={v => set("phone", v)}
                    className="h-12 border-slate-700 bg-slate-900"
                  />
                </div>
                <Button type="submit" className="w-full h-12 rounded-xl bg-gradient-to-r from-blue-600 to-violet-600 text-white font-semibold text-base border-0 mt-2">
                  Continue
                </Button>
              </form>
            </>
          )}

          {step === "plan" && (
            <>
              <button onClick={() => setStep("company")} className="text-slate-400 hover:text-white flex items-center gap-1.5 text-sm mb-8 transition-colors">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <h1 className="text-3xl font-bold text-white mb-2">Choose your plan</h1>
              <p className="text-slate-400 mb-6">Select the plan that fits your team</p>

              {/* Billing cycle toggle */}
              <div className="flex items-center justify-center mb-6">
                <div className="flex items-center bg-slate-800 border border-slate-700 rounded-xl p-1 gap-1">
                  <button
                    type="button"
                    onClick={() => setBillingCycle("monthly")}
                    className={cn(
                      "px-4 py-1.5 rounded-lg text-sm font-medium transition-all",
                      billingCycle === "monthly"
                        ? "bg-blue-600 text-white shadow"
                        : "text-slate-400 hover:text-white"
                    )}
                  >
                    Monthly
                  </button>
                  <button
                    type="button"
                    onClick={() => setBillingCycle("yearly")}
                    className={cn(
                      "px-4 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5",
                      billingCycle === "yearly"
                        ? "bg-blue-600 text-white shadow"
                        : "text-slate-400 hover:text-white"
                    )}
                  >
                    Yearly
                    <span className={cn(
                      "text-xs font-bold px-1.5 py-0.5 rounded-full",
                      billingCycle === "yearly" ? "bg-emerald-400 text-emerald-900" : "bg-emerald-600/30 text-emerald-400"
                    )}>
                      Save 20%
                    </span>
                  </button>
                </div>
              </div>

              <div className="space-y-3 mb-8">
                {PLANS.map(plan => {
                  const displayPrice = billingCycle === "yearly"
                    ? `$${plan.priceYearly}/yr`
                    : `$${plan.priceMonthly}/mo`;
                  const subPrice = billingCycle === "yearly"
                    ? `≈ $${Math.round((plan.priceYearly ?? 0) / 12)}/mo billed annually`
                    : null;
                  return (
                    <button
                      key={plan.id}
                      type="button"
                      onClick={() => set("plan", plan.id)}
                      className={cn(
                        "w-full text-left p-4 rounded-xl border transition-all relative",
                        form.plan === plan.id
                          ? "border-blue-500 bg-blue-500/10"
                          : "border-slate-700 hover:border-slate-500"
                      )}
                    >
                      {plan.popular && (
                        <div className="absolute top-3 right-3 text-xs font-bold bg-blue-600 text-white px-2 py-0.5 rounded-full">Popular</div>
                      )}
                      <div className="flex items-center gap-3">
                        <div className={cn("w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0",
                          form.plan === plan.id ? "border-blue-500 bg-blue-500" : "border-slate-600"
                        )}>
                          {form.plan === plan.id && <Check className="w-2.5 h-2.5 text-white" />}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-baseline gap-2">
                            <span className="font-semibold text-white">{plan.label}</span>
                            <span className="text-blue-400 text-sm font-medium">{displayPrice}</span>
                            {billingCycle === "yearly" && (
                              <span className="text-xs text-emerald-400 font-medium">2 months free</span>
                            )}
                          </div>
                          <div className="text-xs text-slate-400 mt-0.5">{plan.desc}</div>
                          {subPrice && <div className="text-xs text-slate-500 mt-0.5">{subPrice}</div>}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
              <Button
                onClick={handlePlanNext}
                className="w-full h-12 rounded-xl bg-gradient-to-r from-blue-600 to-violet-600 text-white font-semibold text-base border-0"
              >
                Continue to payment
              </Button>
            </>
          )}

          {step === "payment" && (
            <>
              <button onClick={() => setStep("plan")} className="text-slate-400 hover:text-white flex items-center gap-1.5 text-sm mb-8 transition-colors">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>

              <h1 className="text-3xl font-bold text-white mb-2">Activate your account</h1>
                  <p className="text-slate-400 mb-6">Review your plan and activate</p>

                  <div className="bg-slate-900 border border-slate-700 rounded-2xl p-5 mb-5">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <div className="font-semibold text-white">{selectedPlan.label} Plan</div>
                        <div className="text-sm text-slate-400 mt-0.5">{selectedPlan.desc}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-blue-400">
                          {billingCycle === "yearly"
                            ? `$${selectedPlan.priceYearly}/yr`
                            : `$${selectedPlan.priceMonthly}/mo`}
                        </div>
                        {billingCycle === "yearly" && (
                          <div className="text-xs text-emerald-400 mt-0.5">2 months free</div>
                        )}
                      </div>
                    </div>
                    <div className="border-t border-slate-700 pt-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-400">Company</span>
                        <span className="text-white">{form.companyName}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm mt-1.5">
                        <span className="text-slate-400">Account</span>
                        <span className="text-white">{form.email}</span>
                      </div>
                    </div>
                  </div>

                  {isDemoAccount && (
                    <div className="mb-5 p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-start gap-3">
                      <Check className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                      <div className="text-amber-300/80 text-xs">
                        Your demo data will be preserved when your account is upgraded.
                      </div>
                    </div>
                  )}

                  <div className="mb-5 p-3 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center gap-3">
                    <Shield className="w-4 h-4 text-blue-400 shrink-0" />
                    <div className="text-blue-300/80 text-xs">
                      Secure payment via Moyasar — coming soon. Your account will be activated immediately.
                    </div>
                  </div>

                  <Button
                    onClick={handleActivate}
                    disabled={loading}
                    className="w-full h-12 rounded-xl bg-gradient-to-r from-blue-600 to-violet-600 text-white font-semibold text-base border-0"
                  >
                    {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Activating account...</> : "Activate Account"}
                  </Button>
                  <p className="text-center text-xs text-slate-500 mt-4">
                    Billed {billingCycle === "yearly" ? "annually" : "monthly"} · Cancel anytime
                  </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
